import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    document.addEventListener('visibilitychange', this.#syncMobileVideo, { signal });
    this.#mobileQuery.addEventListener('change', this.#syncMobileVideo, { signal });
    this.#motionQuery.addEventListener('change', this.#syncMobileVideo, { signal });
    Promise.all([customElements.whenDefined('deferred-media'), customElements.whenDefined('slideshow-component')]).then(() => {
      if (signal.aborted) return;
      this.#videoObserver = new IntersectionObserver((entries) => {
        if (signal.aborted) return;
        for (const entry of entries) {
          this.#visibleVideos.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        this.#syncMobileVideo();
      }, { threshold: [0, 0.5, 0.75, 1] });
      this.slideshow?.querySelectorAll('slideshow-slide deferred-media').forEach((media) => {
        this.#videoObserver.observe(media);
      });
    });
  }

  #controller = new AbortController();
  #mobileQuery = matchMedia('(max-width: 749px)');
  #motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
  #videoObserver;
  #visibleVideos = new Map();
  #activeVideo = null;
  #playAttempt = 0;

  #syncMobileVideo = () => {
    let next = null;
    if (this.isConnected && this.#mobileQuery.matches && !this.#motionQuery.matches && !document.hidden) {
      const visible = [...this.#visibleVideos].filter(([media, ratio]) => media.isConnected && ratio >= 0.5);
      visible.sort((a, b) => b[1] - a[1]);
      next = visible[0]?.[0] ?? null;
    }
    if (next === this.#activeVideo) return;
    const attempt = ++this.#playAttempt;
    this.#activeVideo?.pauseMedia();
    this.#activeVideo = next;
    if (!next) return;

    next.loadContent(false, false);
    const video = next.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) {
      next.playMedia();
      return;
    }
    video.muted = true;
    video.playsInline = true;
    video.controls = true;
    if (next.refs.toggleMediaButton) next.refs.toggleMediaButton.hidden = true;
    video.play().then(() => {
      if (attempt !== this.#playAttempt) {
        if (next !== this.#activeVideo) video.pause();
        return;
      }
      next.isPlaying = true;
    }).catch(() => {
      if (attempt === this.#playAttempt) next.isPlaying = false;
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoObserver?.disconnect();
    this.#visibleVideos.clear();
    this.#playAttempt++;
    this.#activeVideo?.pauseMedia();
    this.#activeVideo = null;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
